import { DAEMON, useBrowserOnlyInjectSaga } from '@dbh/redux-extra';
import { select, call, spawn, all, put, fork, takeLatest, takeEvery } from 'redux-saga/effects';
import { makeSelectIsBot } from '@dbh/ssr-data-redux';
import throwInServerSideRendering from '@dbh/throw-in-server-side-rendering';
import { makeSelectSrpCurrentPageHotelsList, HOTELS_LOADED, HOTEL_PACKAGES_LOADED } from '@dbh/hotels-redux';
import { makeSelectShoppingCartDataAndStatus, SHOPPING_CART_LOAD_SUCCESS, SHOPPING_CART_CREATE_SUCCESS, SHOPPING_CART_HAS_CHANGED_ITEM_QUANTITIES } from '@dbh/shopping-carts-redux';
import { trackingScriptsInjected, TRACKING_SCRIPTS_MANAGER_ACTIVE_FOR_BOTS, CAN_FIRE_PAGE_VIEW_TRACKING_EVENT, trackingScriptsManagerDisabled, handleTrackingScriptsManagerDisabled, makeSelectIsTrackingScriptsManagerEnabled, REDUX_AND_SAGA_KEY } from '@dbh/tracking-scripts-redux';
import invariant from 'invariant';
import { PAGE_TYPES, PAGE_TYPES_THAT_USE_SEARCH_RESULTS_PAGE_TEMPLATE } from '@dbh/admindev-constants';
import { makeSelectTemplateCode } from '@dbh/webpages-redux';
import { fireCriteoPixelShoppingCartLoaded, fireCriteoPixelViewHomePage, fireCriteoPixelSearchPages, fireCriteoPixelPackagesLoaded, injectCriteoScript } from '@dbh/tracking-criteo';
import { fireConsentUpdateWithGtag } from '@dbh/cookie-consent-utils';
import { fireAwinJourney, injectAwinScript } from '@dbh/tracking-awin';
import { fireBingPixelPageView } from '@dbh/tracking-bing';
import { fireGtagPageView } from '@dbh/tracking-gtag';
import { fireShoppingCartCreated, fireAddOrRemoveFromCart } from '@dbh/tracking-enhanced-commerce';
import 'prop-types';
import withConformsTo from '@dbh/with-conforms-to-for-production-www';
import { makeSelectIsFirstPageAfterSsr } from '@dbh/routing-redux';
import { makeSelectPixelCustomizations } from '@dbh/site-customizations-redux';
import { injectHotjarScript } from '@dbh/tracking-hotjar';
import { PIXELS } from '@dbh/daybreak-hero-redux';

/**
 * Fires all "shopping cart loaded pixels" (`BookingFormPage`).
 * @yields {Object[]} .
 */function*fireShoppingCartLoadedPixels(){yield spawn(fireCriteoPixelShoppingCartLoaded);}/**
 * Checks whether `fireShoppingCartLoadedPixels` should be called or not. We
 * don't call them if this is the "landing page" or the shopping cart is not
 * loaded.
 * @param {boolean} isFirstPageAfterSsr Is this a "landing page"? Passed by us.
 * @yields {Object[]?} .
 */function*handleShoppingCartLoaded(a){throwInServerSideRendering("handleHotelPackagesLoaded");const b=yield select(makeSelectTemplateCode());if(b!==PAGE_TYPES.BookingFormPage)return;const c=yield select(makeSelectShoppingCartDataAndStatus()),d=c.get("loadedData");a&&!d||(// Note that we accept `isShoppingCartLoaded` to be `false` only when it is
// the "first page after SSR" (landing page), as this saga is called when "pixels
// manager" is enabled in such page. Otherwise, this saga is called on
// `SHOPPING_CART_LOAD_SUCCESS` and the shopping cart must be loaded, otherwise
// there is a programming error if we dispatched `SHOPPING_CART_LOAD_SUCCESS`
// but `makeSelectShoppingCartDataAndStatus` returns `false`.
invariant(!!d,"(`handleDataLayerPushForShoppingCartLoaded`) Expected the shopping cart to be loaded."),yield call(fireShoppingCartLoadedPixels));}

/*
 *
 * Constants: `@dbh/tracking-scripts-invoker`.
 *
 */const REFIRE_CONSENT_UPDATE_BEFORE_PAGE_VIEW=!0;

/**
 * Fires page view tracking scripts (we also call them pixels).
 * @param {Object} event .
 * @param {Object} event.payload .
 * @param {string} event.payload.templateCode .
 * @yields {Object[]?} .
 */function*firePageViewPixels(a){const{payload:{templateCode:b}}=a;REFIRE_CONSENT_UPDATE_BEFORE_PAGE_VIEW&&(yield call(fireConsentUpdateWithGtag)),yield spawn(fireGtagPageView),[PAGE_TYPES.TopMenuHashPage,PAGE_TYPES.HashPage].includes(b)||(yield all([spawn(fireAwinJourney),spawn(fireCriteoPixelViewHomePage),spawn(fireBingPixelPageView)]));}

/**
 * Calls all "hotels loaded pixels".
 * Fires in page types: `SearchResultsPage`, `CategoryCityPage`, `PoiPage`.
 * @yields {Object[]?} .
 */function*fireHotelsLoadedPixels(){yield spawn(fireCriteoPixelSearchPages);}/**
 * Calls the "hotels loaded" pixels, if the current page has the
 * `SearchResultsPage` template and has at least one hotel (the "no search
 * results page" is ignored).
 * @yields {Object[]?} .
 */function*handleHotelsLoaded(){throwInServerSideRendering("handleHotelsLoaded");const a=yield select(makeSelectTemplateCode());if(PAGE_TYPES_THAT_USE_SEARCH_RESULTS_PAGE_TEMPLATE[a]){const a=yield select(makeSelectSrpCurrentPageHotelsList());!a||a.isEmpty()||(yield call(fireHotelsLoadedPixels));}}

/**
 * Fires the "hotel packages loaded" pixels.
 * @yields {Object[]} .
 */function*fireHotelPackagesLoadedPixels(){yield spawn(fireCriteoPixelPackagesLoaded);}/**
 * Calls the "hotel packages loaded" pixels, if the current page is an `HdpPage`.
 * @yields {Object[]?} .
 */function*handleHotelPackagesLoaded(){const a=yield select(makeSelectTemplateCode());a!==PAGE_TYPES.HdpPage||(yield call(fireHotelPackagesLoadedPixels));}

/**
 * Fires the "add to cart" pixels when the shopping cart is created in the
 * browser.
 * @yields {Object[]} .
 */function*handleShoppingCartCreateSuccess(){throwInServerSideRendering("handleShoppingCartCreateSuccess"),yield spawn(fireShoppingCartCreated);}

/**
 * Fires the events to track quantity changes in the shopping cart.
 * @param {Object} shoppingCartHasChangedQuantityEvent .
 * @throws {Error} If invoked in `SSR`.
 * @throws {Error} If the argument is not valid.
 */const handleShoppingCartHasChangedQuantity=withConformsTo("handleShoppingCartHasChangedQuantity",[],function*(a){yield spawn(fireAddOrRemoveFromCart,a);});var handleShoppingCartHasChangedQuantity$1 = handleShoppingCartHasChangedQuantity;

/**
 * Checks if this is the "first page after SSR" and only in that case it
 * calls the generators that fill the data layer and fire the CustomEvents.
 * We need to do this because, in the "first page after SSR", those generators
 * wouldn't be called otherwise, since the events we listen to with `takeLatest`
 * won't fire again in the browser, since they already fired in SSR.
 * @yields {Object[][]?} .
 */function*handleTrackingScriptsManagerInjected(){const a=yield select(makeSelectIsFirstPageAfterSsr());a&&(yield all([handleHotelsLoaded(),handleHotelPackagesLoaded(),handleShoppingCartLoaded(!0)]));}

function*injectTrackingScripts(){throwInServerSideRendering("injectTrackingScripts");const a=yield select(makeSelectPixelCustomizations()),{[PIXELS.HOTJAR]:b,[PIXELS.CRITEO]:c,[PIXELS.AWIN]:d}=a;b&&injectHotjarScript(),c&&(yield call(injectCriteoScript)),d&&(yield call(injectAwinScript)),yield put(trackingScriptsInjected());}

const selectIsTrackingScriptsManagerEnabled=makeSelectIsTrackingScriptsManagerEnabled();function*trackingScriptsManagerRootSaga(){if(throwInServerSideRendering("trackingScriptsManagerRootSaga"),!TRACKING_SCRIPTS_MANAGER_ACTIVE_FOR_BOTS){const a=yield select(makeSelectIsBot());if(a)return}const a=yield select(selectIsTrackingScriptsManagerEnabled);a&&(// This must be called only once, we don't want to inject the scripts multiple times.
yield fork(injectTrackingScripts),yield all([takeLatest(trackingScriptsInjected.type,handleTrackingScriptsManagerInjected),takeLatest(CAN_FIRE_PAGE_VIEW_TRACKING_EVENT,firePageViewPixels),// `HdpPage`, pages using the `SearchResultsPage` template.
takeLatest(HOTELS_LOADED,handleHotelsLoaded),takeLatest(HOTEL_PACKAGES_LOADED,handleHotelPackagesLoaded),// `BookingFormPage`.
takeLatest(SHOPPING_CART_LOAD_SUCCESS,handleShoppingCartLoaded),takeLatest(SHOPPING_CART_CREATE_SUCCESS,handleShoppingCartCreateSuccess),takeLatest(trackingScriptsManagerDisabled.type,handleTrackingScriptsManagerDisabled),takeEvery(SHOPPING_CART_HAS_CHANGED_ITEM_QUANTITIES,handleShoppingCartHasChangedQuantity$1)]));}const injectedSagaConfiguration={key:REDUX_AND_SAGA_KEY,mode:DAEMON};

const injectedSagaConfig={...injectedSagaConfiguration,saga:trackingScriptsManagerRootSaga},useBrowserOnlyInjectTrackingScriptsInvokerSaga=()=>useBrowserOnlyInjectSaga(injectedSagaConfig);var useBrowserOnlyInjectTrackingScriptsInvokerSaga$1 = useBrowserOnlyInjectTrackingScriptsInvokerSaga;

export { useBrowserOnlyInjectTrackingScriptsInvokerSaga$1 as useBrowserOnlyInjectTrackingScriptsInvokerSaga };
